// lib/screenWakeLock.ts

// Check if WakeLockSentinel is available, otherwise define minimal interface
interface WakeLockSentinelCompat {
  readonly released: boolean;
  release(): Promise<void>;
  addEventListener(_type: string, _listener: () => void): void;
}

let wakeLock: WakeLockSentinelCompat | null = null;

export async function requestWakeLock(): Promise<boolean> {
  // Check if Wake Lock API is supported
  if (!('wakeLock' in navigator)) {
    console.warn('Screen Wake Lock API not supported');
    return false;
  }

  try {
    // Type assertion for navigator.wakeLock since it might not be in older DOM types
    const wakeLockApi = (
      navigator as Navigator & {
        wakeLock?: {
          request(_type: 'screen'): Promise<WakeLockSentinelCompat>;
        };
      }
    ).wakeLock;

    if (!wakeLockApi) {
      console.warn('Wake Lock API not available');
      return false;
    }

    wakeLock = await wakeLockApi.request('screen');

    return true;
  } catch (err) {
    console.error('Failed to acquire screen wake lock:', err);
    return false;
  }
}

export async function releaseWakeLock(): Promise<void> {
  if (wakeLock) {
    try {
      await wakeLock.release();
      wakeLock = null;
    } catch (err) {
      console.error('Failed to release screen wake lock:', err);
    }
  }
}

export function isWakeLockActive(): boolean {
  return wakeLock !== null && !wakeLock.released;
}

// Handle visibility change to re-acquire wake lock when user returns to tab
export function handleVisibilityChange(): void {
  if (wakeLock && document.visibilityState === 'visible') {
    // Re-acquire wake lock when tab becomes visible again
    requestWakeLock();
  }
}
